import * as React from "react";

/**
 * Cabeçalho único de página da plataforma.
 *
 * Generalização do `AdminPageHeader`, que nasceu em
 * `components/admin/admin-lista.tsx`, já estava certo e era usado por 2 telas.
 * Antes desta casca cada uma das 50 telas escrevia o próprio cabeçalho — daí
 * os 6 tamanhos de título diferentes no app. `admin-lista.tsx` reexporta o que
 * está aqui, para não quebrar quem já importava de lá.
 *
 * As regras que este arquivo existe para não deixar ninguém reinventar:
 *
 * 1. **Um título por tela**, sempre `text-xl md:text-2xl font-bold`. `text-3xl`
 *    e `text-4xl` não existem mais nas telas do app.
 *
 * 2. **`min-w-0` no bloco de texto e `shrink-0` no bloco de ações.** É esse par
 *    que impede o estouro em 390px: sem `min-w-0` o `truncate` do título não
 *    tem efeito, porque um filho de flex não encolhe abaixo do seu conteúdo.
 *
 * 3. **No máximo UM botão primário.** Exportar é sempre secundário (`outline`),
 *    em toda tela, sem exceção. Se sobrarem três ou mais ações, as extras vão
 *    para um `DropdownMenu` com gatilho `···`.
 *
 * 4. **Sem regra automática de altura em `acao`.** O Radix `Switch` também é um
 *    `<button>`, e um `[&_button]` global transformaria o interruptor num
 *    retângulo. Quem passa `acao` usa `ALVO_TOQUE_BOTAO` no próprio botão.
 */

/** Alvo de toque mínimo (44×44) para botão de ÍCONE dentro de lista. */
export const ALVO_TOQUE = "h-11 w-11";

/**
 * Altura de 44px só no celular, para botão com texto (ex.: "+ Nova" do
 * cabeçalho). No desktop o botão volta à altura natural do variant.
 */
export const ALVO_TOQUE_BOTAO = "min-h-11 md:min-h-0";

export interface PageHeaderProps {
  titulo: string;
  /** Uma linha, sentence case. */
  descricao?: string;
  /** NO MÁXIMO UM primário. */
  acao?: React.ReactNode;
  /** Renderiza à esquerda de `acao`. */
  acoesSecundarias?: React.ReactNode;
  /**
   * Quando o CONTEÚDO fica estreito (≤ 640px), as ações deixam de flutuar à
   * direita e empilham abaixo do título, em largura cheia — a primária (`acao`)
   * ocupa a linha inteira e fica por ÚLTIMO, que é onde o polegar chega.
   *
   * ⚠️ A medida é a do conteúdo, não a da janela: a sidebar come 256px, então
   * uma janela de 1142px ainda tem 886px e as ações continuam na linha do
   * título. Media query de viewport empilhava cedo demais. Quem mede é a
   * container query de `.acoes-header` (index.css), que precisa de um ancestral
   * `.pagina-container` — o `ProdutorShell` do /mercado já o coloca.
   *
   * Nasceu no /mercado (PR 1 da UI do produtor); é opt-in para não mexer nas
   * telas que já cabem com o botão ao lado do título.
   */
  empilharQuandoEstreito?: boolean;
  /** Acima do título — "Compradores / Humberto". */
  breadcrumb?: React.ReactNode;
}

export function PageHeader({
  titulo,
  descricao,
  acao,
  acoesSecundarias,
  empilharQuandoEstreito,
  breadcrumb,
}: PageHeaderProps) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      {/* Com `empilharQuandoEstreito`, o bloco de texto é `flex-1` (base 0): ele
          ENCOLHE — o subtítulo quebra em duas linhas — em vez de reservar a
          largura máxima da frase e empurrar as ações para baixo. Era isso que
          fazia Corretores quebrar por 3px em 1142px. Sem a prop, o comportamento
          antigo fica intacto (Vendas e as outras 50 telas). */}
      <div className={empilharQuandoEstreito ? "min-w-0 flex-1" : "min-w-0"}>
        {breadcrumb && (
          <div className="text-xs text-muted-foreground mb-0.5 truncate">{breadcrumb}</div>
        )}
        <h1 className="text-xl md:text-2xl font-bold truncate">{titulo}</h1>
        {descricao && (
          <p className="text-sm text-muted-foreground mt-0.5">{descricao}</p>
        )}
      </div>
      {/* Sem regra automática de altura aqui: o Radix Switch também é um
          <button>, e um `[&_button]` global transformaria o interruptor num
          retângulo. Quem passa `acao` usa `ALVO_TOQUE_BOTAO` no próprio botão. */}
      {(acoesSecundarias || acao) && (
        empilharQuandoEstreito ? (
          // As duas aparências (linha única / empilhado) saem de `.acoes-header`
          // em index.css, decidida por CONTAINER query — ver o comentário da prop.
          <div className="acoes-header" data-testid="page-header-acoes">
            {acoesSecundarias}
            {acao && <div className="acoes-header-primaria">{acao}</div>}
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {acoesSecundarias}
            {acao}
          </div>
        )
      )}
    </div>
  );
}

/** Nome antigo, mantido para não quebrar as telas que já o adotaram. */
export const AdminPageHeader = PageHeader;
